import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger, Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from '@music-vine/cadence/ui';
import Link from 'next/link';
import { useCallback, useState } from 'react';
interface RemoveSafelistDialogProps {
  channelName: string;
  children: React.ReactNode;
  isRemoving?: boolean;
  onConfirm: () => void;
}
const HELP_CENTER_URL = '/help-center/uppbeat-essentials-creator-pro?type=how-does-safelisting-work';
const DialogContentStyled = ({
  children,
  className,
  ...props
}: React.ComponentProps<typeof DialogContent>) => <DialogContent className={cn('min-h-40 w-[90%] xs:w-[400px] gap-4 rounded-xl border-gray-100 border-solid bg-white p-6 shadow-md 2xl:w-[482px] dark:border-1 dark:border-gray-800 dark:bg-gray-950 dark:shadow-xl', className)} {...props} data-sentry-element="DialogContent" data-sentry-component="DialogContentStyled" data-sentry-source-file="RemoveSafelistDialog.tsx">
    {children}
  </DialogContent>;
const DialogHeader = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn('font-heading font-semibold text-xl tracking-tight dark:text-white', className)} data-sentry-component="DialogHeader" data-sentry-source-file="RemoveSafelistDialog.tsx">
    {children}
  </div>;
const DialogConfirmationContent = ({
  channelName
}: {
  channelName: string;
}) => <div className="flex flex-col gap-3" data-sentry-component="DialogConfirmationContent" data-sentry-source-file="RemoveSafelistDialog.tsx">
    <p className="text-gray-800 text-sm dark:text-gray-300">
      Are you sure you want to remove{' '}
      <span className="font-semibold">{channelName}</span> from the safelist?
    </p>
    <p className="text-gray-800 text-sm dark:text-gray-300">
      Future videos may receive copyright claims without{' '}
      <Link className="text-pink-500 underline hover:text-pink-600 dark:text-white hover:dark:text-white" href={HELP_CENTER_URL} data-sentry-element="Link" data-sentry-source-file="RemoveSafelistDialog.tsx">
        proper credit
      </Link>
      .
    </p>
  </div>;
const DrawerConfirmationContent = ({
  channelName
}: {
  channelName: string;
}) => <p className="text-center text-gray-800 text-sm dark:text-gray-300" data-sentry-component="DrawerConfirmationContent" data-sentry-source-file="RemoveSafelistDialog.tsx">
    Are you sure you want to remove{' '}
    <span className="font-semibold">{channelName}</span> from the safelist?
    Future videos may receive copyright claims without{' '}
    <Link className="text-pink-500 underline hover:text-pink-600" href={HELP_CENTER_URL} data-sentry-element="Link" data-sentry-source-file="RemoveSafelistDialog.tsx">
      proper credit
    </Link>
    .
  </p>;
export const RemoveSafelistDialog = ({
  channelName,
  onConfirm,
  isRemoving,
  children
}: RemoveSafelistDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    isMd
  } = useTWScreens();
  const handleOnOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);
  const onCancel = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleConfirm = useCallback(() => {
    onConfirm();
    setIsOpen(false);
  }, [onConfirm]);
  if (isMd) {
    return <Dialog onOpenChange={handleOnOpenChange} open={isOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContentStyled>
          <DialogTitle className="sr-only">
            Remove safelist protection
          </DialogTitle>
          <DialogDescription className="sr-only">
            Confirm removal of channel from safelist
          </DialogDescription>
          <div className="flex flex-col gap-4">
            <DialogHeader>Remove safelist protection</DialogHeader>
            <DialogConfirmationContent channelName={channelName} />
            <div className="mt-2 flex w-full flex-col-reverse gap-3 md:flex-row md:justify-end">
              <Button border className="border-gray-150! dark:border-gray-800!" onClick={onCancel} size="lg" variant="transparent">
                Cancel
              </Button>
              <Button className="font-semibold" disabled={isRemoving} onClick={handleConfirm} size="lg" variant="brand">
                {isRemoving ? 'Removing...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </DialogContentStyled>
      </Dialog>;
  }
  return <Drawer onOpenChange={handleOnOpenChange} open={isOpen} data-sentry-element="Drawer" data-sentry-component="RemoveSafelistDialog" data-sentry-source-file="RemoveSafelistDialog.tsx">
      <DrawerTrigger asChild data-sentry-element="DrawerTrigger" data-sentry-source-file="RemoveSafelistDialog.tsx">{children}</DrawerTrigger>
      <DrawerContent className={cn('h-auto px-6 outline-hidden ring-0 focus:outline-hidden focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0')} data-sentry-element="DrawerContent" data-sentry-source-file="RemoveSafelistDialog.tsx">
        <DrawerHeader className="p-0" data-sentry-element="DrawerHeader" data-sentry-source-file="RemoveSafelistDialog.tsx">
          <DrawerTitle className="sr-only" data-sentry-element="DrawerTitle" data-sentry-source-file="RemoveSafelistDialog.tsx">
            Remove safelist protection
          </DrawerTitle>
          <DrawerDescription className="sr-only" data-sentry-element="DrawerDescription" data-sentry-source-file="RemoveSafelistDialog.tsx">
            Confirm removal of channel from safelist
          </DrawerDescription>
        </DrawerHeader>
        <div className="flex flex-col items-center justify-center gap-5">
          <div>
            <div className="relative flex w-full items-center justify-center">
              <div className="mb-2 text-center font-heading font-semibold text-black text-xl tracking-tight dark:text-white">
                Remove safelist protection
              </div>
            </div>
            <DrawerConfirmationContent channelName={channelName} data-sentry-element="DrawerConfirmationContent" data-sentry-source-file="RemoveSafelistDialog.tsx" />
          </div>
          <div className="mt-2 flex w-full flex-col-reverse gap-3 md:flex-row md:justify-end">
            <Button className="font-semibold" onClick={onCancel} size="lg" variant="subtle" data-sentry-element="Button" data-sentry-source-file="RemoveSafelistDialog.tsx">
              Cancel
            </Button>
            <Button className="font-semibold" disabled={isRemoving} onClick={handleConfirm} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="RemoveSafelistDialog.tsx">
              {isRemoving ? 'Removing...' : 'Confirm'}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>;
};